import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

const Card = ({
  children,
  title,
  subtitle,
  actions,
  variant = 'default',
  padding = 'md',
  hoverable = false,
  className,
  headerClassName,
  bodyClassName,
  ...props
}) => {
  const baseStyles = 'bg-white dark:bg-neutral-800 rounded-2xl transition-all duration-200';

  const variantStyles = {
    default: 'border border-neutral-200 dark:border-neutral-700/60 shadow-card',
    elevated: 'border border-neutral-200 dark:border-neutral-700/60 shadow-md',
    outlined: 'border-2 border-neutral-200 dark:border-neutral-700',
    flat: 'border-0',
  };

  const paddingStyles = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const hoverStyles = hoverable
    ? 'hover:shadow-card-hover hover:border-neutral-300 dark:hover:border-neutral-600 cursor-pointer hover:-translate-y-0.5'
    : '';

  const cardClasses = clsx(baseStyles, variantStyles[variant], paddingStyles[padding], hoverStyles, className);

  const hasHeader = title || actions;

  return (
    <motion.div
      className={cardClasses}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
      {...props}
    >
      {hasHeader && (
        <div className={clsx('flex items-start justify-between', padding !== 'none' && 'mb-5', headerClassName)}>
          {title && (
            <div className="flex-1">
              <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
              {subtitle && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{subtitle}</p>
              )}
            </div>
          )}
          {actions && <div className="flex items-center gap-2 ml-4">{actions}</div>}
        </div>
      )}
      <div className={clsx(bodyClassName)}>{children}</div>
    </motion.div>
  );
};

Card.Header = ({ children, className }) => (
  <div className={clsx('border-b border-neutral-100 dark:border-neutral-700/60 pb-4 mb-5', className)}>
    {children}
  </div>
);

Card.Body = ({ children, className }) => (
  <div className={clsx('', className)}>{children}</div>
);

Card.Footer = ({ children, className }) => (
  <div className={clsx('border-t border-neutral-100 dark:border-neutral-700/60 pt-4 mt-5', className)}>
    {children}
  </div>
);

export default Card;
